// V4 JOB SUGGESTED CANDIDATES ********************************************************************
// pathname: /v4/{jobSlug}/suggested-candidates
// ************************************************************************************************
import { useSwrInfiniteApi } from '@hooks/useSwrApi';


// Interface
// ********************************************
interface SuggestedCandidatesProps {
  locale?: string;
  jobSlug: string;
  serverSideData?: ApiSchema<'ResumeResult2'> | ApiSchema<'ErrorResponse'>;
  limit?: number;
  urlParams?: string;
  successCallback?: (data?: ApiSchema<'ResumeResult2'>[]) => void;
  shouldFetch?: boolean;
  initialCursor?: string;
}


/**
 * @description SWR: fetch job's suggested candidates with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **jobSlug**: string. The job slug to use for the request.
 * * **serverSideData**: ApiTalentPageV3, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (i.e.: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of CVs to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **urlParams**: '&sort=relevant', optional. Default empty string.
 * * **successCallback**: function, optional. A function to call when the data is successfully fetched. Default undefined.
 * * **shouldFetch**: boolean, optional. If true, the data will be fetched. Default true.
 * * **initialCursor**: string, optional. The initial cursor to use for the request. Default undefined.
 */
export const useSwrSuggestedCandidatesV4 = (props: SuggestedCandidatesProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    jobSlug,
    serverSideData,
    limit = 24,
    urlParams = '',
    successCallback,
    shouldFetch,
    initialCursor,
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'SuggestedCandidateResult'>>({
    url: `/v4/${jobSlug}/suggested-candidates`,
    locale,
    urlParams,
    serverSideData,
    shouldFetch,
    infiniteConfig: {
      paginationType: 'cursor',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      initialCursor,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      onSuccess: (newData) => {
        if (successCallback) {
          successCallback(newData);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  // Cursor-based helper: advance to next cursor page
  const fetchNextCursor = () => setSize((sizeAsRef?.current ?? size) + 1);


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
    fetchNextCursor,
  };
};
